<template>
  <div
    class="letter-selection min-h-[100dvh] max-h-[100dvh] overflow-hidden flex flex-col bg-transparent relative"
  >
    <!-- FitToHeight scales the board to the viewport so both tile rows, the
         timer, status, and controls stay visible without scrolling (issue #289). -->
    <FitToHeight class="flex-1 min-h-0">
      <main
        class="letter-selection-main flex flex-col items-center gap-3 py-3 px-4 max-w-[1200px] mx-auto w-full box-border md:gap-8 md:py-8 md:px-6 lg:gap-8 lg:p-8"
        role="main"
        aria-labelledby="letter-selection-main-heading"
      >
        <!-- Page heading kept for screen readers (no visible hero banner, to stay
           consistent with the other in-game screens). -->
        <h1 id="letter-selection-main-heading" class="sr-only">
          {{ STRINGS.ui.titles.letterSelectionGame }}
        </h1>

        <section
          role="region"
          aria-labelledby="letter-grid-heading"
          class="w-full flex justify-center items-center shrink-0"
        >
          <h4 id="letter-grid-heading" class="sr-only">{{ STRINGS.ui.titles.selectedLetters }}</h4>
          <!-- Shared arena-card frame so the tiles sit in the same surface as the
             word-finding screen (consistent framing across the two phases). -->
          <div class="rd-card w-full max-w-[500px] mx-auto">
            <LetterGrid
              :letters="visibleLetters as Letter[]"
              :current-slot="visibleLetters.length"
              :animation-state="animationState"
            />
          </div>
        </section>

        <!-- Everything that is not the letter grid. Grouped so the phase
             handover can step it back on its own, leaving the grid as the only
             thing still moving (#438). `gap: inherit` keeps the spacing
             identical to when these were direct children of the main. -->
        <div class="ls-chrome">
          <section
            v-if="timerIsActive || timerTimeRemaining > 0"
            role="region"
            aria-labelledby="selection-timer-heading"
            class="w-full flex justify-center items-center"
          >
            <h4 id="selection-timer-heading" class="sr-only">Selection Timer</h4>
            <SelectionTimer
              :time-remaining="timerTimeRemaining"
              :total-time="timerTotalTime"
              :is-active="timerIsActive"
            />
          </section>

          <section
            role="region"
            aria-labelledby="game-status-heading"
            class="w-full flex justify-center items-center"
          >
            <h4 id="game-status-heading" class="sr-only">{{ STRINGS.ui.titles.gameStatus }}</h4>
            <PlayerStatus
              :players="players"
              :current-selector="letterSelection.currentSelector.value"
              :current-player-id="currentPlayerId"
              :status="letterSelection.selectionStatus.value"
              :vowel-count="letterSelection.vowelCount.value"
              :consonant-count="letterSelection.consonantCount.value"
            />
          </section>

          <section
            role="region"
            aria-labelledby="controls-heading"
            class="w-full flex justify-center items-center"
          >
            <h4 id="controls-heading" class="sr-only">
              {{ STRINGS.ui.titles.letterSelectionControls }}
            </h4>
            <SelectionControls
              :vowel-enabled="letterSelection.vowelButtonEnabled.value"
              :consonant-enabled="letterSelection.consonantButtonEnabled.value"
              :vowel-count="letterSelection.vowelCount.value"
              :consonant-count="letterSelection.consonantCount.value"
              :is-player-turn="letterSelection.isPlayerTurn.value"
              :status="letterSelection.selectionStatus.value"
              @select-letter="handleLetterSelection"
              @auto-select="handleAutoSelect"
            />
          </section>
        </div>
      </main>
    </FitToHeight>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import LetterGrid from './LetterGrid.vue';
import SelectionControls from './SelectionControls.vue';
import SelectionTimer from './SelectionTimer.vue';
import PlayerStatus from './PlayerStatus.vue';
import FitToHeight from './FitToHeight.vue';
import { useWebSocket } from '@/composables/useWebSocket';
import { randomUuid } from '@/utils/uuid';
import { useLetterSelection } from '@/composables/useLetterSelection';
import { useLetterRevealQueue } from '@/composables/useLetterRevealQueue';
import { GAME_CONFIG } from '@/config/constants';
import { STRINGS } from '@/constants/strings';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';
import type { Player, Letter, LetterKind, SelectionStatus, ClientMessage } from '@/types/generated';

interface Props {
  gameId: string;
  players: Player[];
  currentPlayerId: string;
  selectedLetters: Letter[];
  // When true, show the current letters instantly with no reveal animation
  // (reconnection restore). Live selections animate.
  revealInstantly?: boolean;
  selectorPlayerId: string | null;
  selectionEnabled: boolean | null;
  vowelButtonEnabled: boolean | null;
  consonantButtonEnabled: boolean | null;
  selectionStatus: SelectionStatus | null;
  timerTimeRemaining: number;
  timerTotalTime: number;
  timerIsActive: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  revealInstantly: false,
});

const emit = defineEmits<{
  error: [message: string];
}>();

// WebSocket composable
const { sendMessage, isConnected } = useWebSocket();

// Letter selection business logic composable
const letterSelection = useLetterSelection({
  gameId: props.gameId,
  players: props.players,
  currentPlayerId: props.currentPlayerId,
});

// Letter reveal queue - staggers the client-side reveal animation.
const allLettersRef = computed(() => props.selectedLetters);
const revealInstantlyRef = computed(() => props.revealInstantly);
const { visibleLetters, isRevealing } = useLetterRevealQueue(allLettersRef, {
  revealInstantly: revealInstantlyRef,
  onQueueDrained: () => {
    // All 10 letters revealed - signal server we're ready
    if (!isConnected.value) return;
    const message: ClientMessage = {
      ReadyForFinding: { game_id: props.gameId },
    };
    sendMessage(message);
  },
});

// Map isRevealing to the existing animation state contract.
//
// isRevealing is tested first because the last letter becoming visible and the
// board reaching TOTAL_LETTERS happen on the same tick: checking the count
// first reported 'complete' while the tenth tile was still meant to be
// flipping, and LetterGrid only animates the tile at `currentSlot` while the
// state is 'revealing', so that tile appeared with no reveal at all. After the
// final reveal the queue waits out a stagger before it even starts its settle
// timer, so isRevealing stays true for a stagger plus LETTER_REVEAL_DURATION —
// more than enough to cover the flip — and then yields to 'complete'.
const animationState = computed<'idle' | 'revealing' | 'complete'>(() => {
  if (isRevealing.value) return 'revealing';
  if (visibleLetters.value.length >= GAME_CONFIG.TOTAL_LETTERS) return 'complete';
  return 'idle';
});

// Watch selection props from App.vue (driven by GameSnapshot handler)
watch(
  () =>
    [
      props.selectedLetters,
      props.selectorPlayerId,
      props.selectionEnabled,
      props.vowelButtonEnabled,
      props.consonantButtonEnabled,
      props.selectionStatus,
    ] as const,
  ([letters, selector, enabled, vowelEnabled, consonantEnabled, status]) => {
    letterSelection.setSelectionState({
      selectedLetters: letters,
      selectionEnabled: enabled,
      vowelButtonEnabled: vowelEnabled,
      consonantButtonEnabled: consonantEnabled,
      selectionStatus: status,
      selectorPlayerId: selector,
    });
  },
  { immediate: true }
);

// A tap that never reaches the server. Nothing is raised in the UI: connection
// loss has exactly one owner — useConnectionGiveUp — and the terminal error
// screen this used to reach re-entered matchmaking. The socket is not OPEN, so
// either it returns inside the window and the player can tap again, or they go
// home with a notice.
//
// It is reported because it is otherwise invisible: `isConnected` tracks
// `connectionStatus`, which a close handler updates, while sendMessage guards
// on `ws.readyState`. A drop in that gap loses the tap while the app still
// believes it is connected, and no give-up window has been armed yet.
const noteDroppedSend = (kind: string) => {
  reportClientError(
    new Error(`LetterSelection: ${kind} not sent (gameId=${props.gameId})`),
    ErrorLocation.LetterSelectionSendDropped
  );
};

const handleLetterSelection = (letterType: LetterKind) => {
  if (!isConnected.value) {
    noteDroppedSend('SelectLetter');
    return;
  }

  // Validate selection using business logic
  const validation = letterSelection.validateLetterSelection(letterType);
  if (!validation.isValid) {
    if (validation.reason !== 'Not your turn') {
      emit('error', validation.reason || 'Invalid selection');
    }
    return;
  }

  // Send selection to server with idempotency token
  const message: ClientMessage = {
    SelectLetter: {
      game_id: props.gameId,
      letter_type: letterType,
      idempotency_token: randomUuid(),
    },
  };

  if (!sendMessage(message)) noteDroppedSend('SelectLetter');
};

// Handle auto-select ("pick for me"): ask the server to deal all remaining
// letters at once. The server validates the selector and enforces the
// vowel/consonant caps, so no client-side letter validation is needed — the
// existing snapshot → reveal-queue path renders the staggered reveal.
const handleAutoSelect = () => {
  if (!isConnected.value) {
    noteDroppedSend('AutoSelectLetters');
    return;
  }

  const message: ClientMessage = {
    AutoSelectLetters: {
      game_id: props.gameId,
    },
  };

  if (!sendMessage(message)) noteDroppedSend('AutoSelectLetters');
};

onMounted(() => {
  if (!isConnected.value) {
    console.warn('[LetterSelection] WebSocket not connected, component may not function properly');
  }
});
</script>
